/**
 * Conversation Page Template
 *
 * Displays conversation messages, handles user input, and hosts the
 * prototype + streamed-artifact side panels and the findings panel.
 *
 * The facade encapsulates the shared logic:
 * - State (messages, isLoading, isStreaming, headers)
 * - Streaming (submitMessage, cancelStreaming, loadHistory)
 * - Headers (clarification, reflection, repeated, readyToCreate)
 * - Prototyper (build-stream, snapshot, panel)
 * - Project (create, metadata)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  ConversationPageFacade,
  Finding,
  FindingAction,
  PrototyperBuildTimeline,
  QuestionAnswerBody,
} from '@ideoz/core';
import {
  artifactService,
  buildArtifactPublicPageUrl,
  messageHasPrototyperArtifact,
  projectService,
  selectProjects,
  useAppSelector,
  useCreditLimitBanner,
  useTrackEvent,
} from '@ideoz/core';
import { asMessageArtifact } from '../artifacts/artifactMappers';
import { PRODUCT_TYPE_OPTIONS } from '../designSystem/designSystemConstants';
import { canRepairInPlace } from '../prototyper/utils/crashRepairTarget';
import {
  Header,
  showToast,
  SideMenu,
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
  DesignInsightsPanel,
  hasArabicCharacters,
  useScrollToBottom,
  InteractiveQuestionCard,
  Button,
  NotFoundState,
  cn,
} from '@ideoz/ui';
import { Sparkles, CirclePlus } from 'lucide-react';
import {
  WORKBENCH_BUILD_MISSING_VERSION_EVENT,
  WORKBENCH_FIX_PREVIEW_CRASH_EVENT,
  buildPreviewCrashFixPrompt,
  type WorkbenchPreviewCrash,
} from '@ideoz/workbench';
import { ConversationDialogs } from './components/ConversationDialogs';
import { ConversationComposer } from './components/ConversationComposer';
import { useConversation } from './context/ConversationContext';
import { useConversationFindings } from './hooks/useConversationFindings';
import { useConversationPrototypeStream } from './hooks/useConversationPrototypeStream';
import { useConversationTurns } from './hooks/useConversationTurns';
import { ScopeIntelligenceRail } from './components/ScopeIntelligenceRail';
import { MessagesPane } from './components/MessagesPane';
import { PrototyperPane } from './components/PrototyperPane';
import { useConversationSound } from './hooks/useConversationSound';
import { useConversationHistoryView } from './hooks/useConversationHistoryView';
import { useStreamedArtifactPanel } from './hooks/useStreamedArtifactPanel';
import { useHotEditAskDraft } from './hooks/useHotEditAskDraft';
import {
  usePrototypePanel,
  PROTOTYPE_PANEL_DEFAULT_PERCENT,
  CONVERSATION_PANEL_DEFAULT_PERCENT,
} from './hooks/usePrototypePanel';

// ============================================================================
// INTERFACES
// ============================================================================

// Props are provided via ConversationProvider (see ./context/ConversationContext) —
// ConversationModule takes no props. The workbench wiring, auth dialog, facade,
// and scope-nav callbacks all live on the context value.

// ============================================================================
// COMPONENT
// ============================================================================

export function ConversationModule() {
  const hotEditAskDraft = useHotEditAskDraft();
  // Everything comes from the ConversationProvider (see ConversationPage) — no
  // props are drilled in. Workbench fields are destructured into the same local
  // names the body already uses, so nothing below this line changed.
  const {
    facade,
    isWeb,
    authDialogState,
    urlConversationId,
    onBack,
    onConversationNotFound,
    workbench,
    openArtifactIdFromLibrary,
    openArtifactInteractionId,
  } = useConversation();
  // Scope routes name the param `conversationId`. `urlConversationId` only
  // sees a route param called `id`, which these pages do not have.
  const { conversationId: routeConversationId, id: routeId } = useParams<{
    conversationId?: string;
    id?: string;
  }>();
  const libraryConversationId = routeConversationId || routeId || urlConversationId || null;
  const {
    renderWorkbenchPanel,
    workbenchToolActivity,
    isStreamingPrototype = false,
    streamingPublishReady = false,
    streamingPreviewReady = false,
    onStreamingPrototypeStart,
    workbenchSnapshotBootstrap,
    workbenchSnapshotRestoreError,
    workbenchSnapshotIndexLoading,
    getWorkbenchSnapshotIndexEntry,
    onRetrySnapshotBootstrap,
    activeSnapshotInteractionId,
    onPrototypePublished,
    restoreInteractionLoadingId,
    streamingWorkbenchLoading = false,
    onStreamingWorkbenchPrototypePanelVisibilityChange,
    streamingWorkbenchLlmInteractionId = null,
    livePrototyperBuildTimeline = null,
    isWorkbenchBuildLive = false,
    workbenchTimelineTerminalVisible = false,
    onPrototyperWorkbenchRestore,
    uploadVerifiedPrototypeSnapshot,
    onCancelWorkbenchStream,
    openPrototypePanelFromLibrary,
  } = workbench;
  const { trackEvent } = useTrackEvent();
  // Use onboarding flag from Redux facade
  const isOnboardingUser = facade.state.isOnboardingPrototyper === true;
  // ========== Local UI State ==========
  const [showBanner, setShowBanner] = useState(true);
  const creditLimitBanner = useCreditLimitBanner();

  // Scope pages start with the sidebar expanded so the Recent chats / + New chat list is visible.
  // Other pages keep their existing behavior (collapsed by default).
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    facade?.sidebar?.pageType !== 'scope'
  );
  const [isFigJamPluginDialogOpen, setIsFigJamPluginDialogOpen] = useState(false);
  const [isUpgradeToSmartDialogOpen, setIsUpgradeToSmartDialogOpen] = useState(false);
  const [hasProcessedUserAction, setHasProcessedUserAction] = useState(false);

  const conversationContainerRef = useRef<HTMLDivElement>(null);
  const chatBoxContainerRef = useRef<HTMLDivElement>(null);
  // Track if prototype was initiated from uncover mode (to prevent mode switch in menu)
  const initiatedFromUncoverRef = useRef(false);

  // ========== Extract Facade State ==========
  const {
    isLoggedIn,
    currentProjectId,
    currentScopeId,
    currentConversationId,
    messages,
    isLoading,
    isLoadingDetails,
    isStreaming,
    name,
    session_id,
    init_message,
    skip_analysis,
    initialAttachmentIds,
    isActive,
    llmFamily,
    planDetails,
    initiatedFromUncover,
  } = facade.state;

  const { t } = useTranslation();
  const projects = useAppSelector(selectProjects);

  // Product types the founder selected in the Design System (persisted as a
  // comma-joined `projectType` on the project). Ordered by the canonical option
  // list so "website" is never first; the first entry is the Generate Prototype
  // dropdown's default target. Empty when nothing is selected → the composer
  // keeps its single Generate Prototype item.
  const listedProject = useMemo(
    () => (projects ?? []).find((p: { id?: string }) => p.id === currentProjectId),
    [projects, currentProjectId]
  );
  // A conversation opened from a link arrives before any page has loaded the project list, so
  // the menu offered only a plain Generate Prototype, with no Website / Web App / Mobile choice
  // (2 Oct 2026). The project is fetched on its own then, and kept here rather than in the
  // store: the Design System reads the store's first project as the startup's.
  const [fetchedProjectType, setFetchedProjectType] = useState<{ id: string; type: string }>();
  useEffect(() => {
    if (!currentProjectId || listedProject || fetchedProjectType?.id === currentProjectId) return;
    let cancelled = false;
    projectService
      .getProject(currentProjectId)
      .then((project) => {
        if (cancelled) return;
        const type = (project as { projectType?: string } | undefined)?.projectType ?? '';
        setFetchedProjectType({ id: currentProjectId, type });
      })
      .catch(() => {
        // The menu keeps its single Generate Prototype item, as before.
      });
    return () => {
      cancelled = true;
    };
  }, [currentProjectId, listedProject, fetchedProjectType?.id]);

  const prototypeProductTypes = useMemo(() => {
    const projectType =
      (listedProject as { projectType?: string } | undefined)?.projectType ??
      (fetchedProjectType?.id === currentProjectId ? fetchedProjectType.type : '');
    const selected = new Set(
      String(projectType ?? '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    );
    return PRODUCT_TYPE_OPTIONS.filter((option) => selected.has(option.value)).map((option) => ({
      value: option.value,
      label: t(`designSystem.opt.productType.${option.value}`, { defaultValue: option.label }),
    }));
  }, [listedProject, fetchedProjectType, currentProjectId, t]);

  /**
   * Live pointer to the conversation actually on screen — refreshed every render, unlike
   * the `currentConversationId` closed over inside a submit callback (which stays frozen at
   * whatever it was when that particular message was sent). Reply-completion callbacks below
   * compare against this to detect "the user has since navigated to a different conversation"
   * and skip applying that reply's header flags (clarify/prototyper/ready-to-create) — otherwise
   * a background reply that finishes after a chat switch can silently flip the *new* chat into
   * Prototyper mode.
   */
  const activeConversationIdRef = useRef(currentConversationId);
  useEffect(() => {
    activeConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  /**
   * True if a reply that was submitted against `submittedConversationId` still belongs to the
   * conversation currently on screen. A falsy `submittedConversationId` means the message was
   * sent before a brand-new conversation had an id yet — that's only "stale" once the user has
   * navigated to some *other* already-known conversation, not merely because the id hasn't been
   * assigned/synced yet.
   */
  const isReplyStillActive = useCallback(
    (submittedConversationId: string | null | undefined, newConversationId?: string): boolean => {
      const active = activeConversationIdRef.current;
      if (submittedConversationId) {
        return active === submittedConversationId;
      }
      return active == null || active === newConversationId;
    },
    []
  );

  const { isClarification, isReflection, isRepeated, isPrototyper, isReadyToCreate } =
    facade.headers;

  // Get current mode from facade
  const { currentMode } = facade.mode;

  // SSE-streamed artifact side panel (open artifact, sync, save/share).
  const streamedArtifact = useStreamedArtifactPanel({
    messages,
    conversationMessages: facade.streaming.conversationState.messages,
  });

  // ===== Scope Intelligence / findings ==================================
  // All findings state + logic lives in the hook. The rail renders the durable
  // panel; the message list reads `inlineFindingsByMessageId` to anchor findings
  // that surfaced live during a turn under that turn's assistant message.
  const findings = useConversationFindings();
  const {
    inlineFindingsByMessageId,
    resolvingFindingId,
    handleFindingAction,
    isFindingsCollapsed,
    isFindingsOpen,
  } = findings;

  const {
    clearError: clearPrototyperError,
    currentPrototyper,
    fetchPrototyperById,
  } = facade.prototyper;

  const currentPrototypeId = currentPrototyper?.prototyper_demo_link_id ?? null;
  const currentPrototypeSlug = currentPrototyper?.slug ?? null;
  const isPrototypeCreating = isStreamingPrototype ? isWorkbenchBuildLive : false;

  // Check if user has credits remaining
  // Base credits are ALWAYS required first
  // In prototyper mode: also check prototype credits
  const prototypeCredit = planDetails?.credits?.find((c: any) => c.type === 'prototype');
  const prototypeBalance = prototypeCredit != null ? prototypeCredit.balance : 1; // If no prototype credit in planDetails, allow (don't block)

  const baseCredit = planDetails?.credits?.find((c: any) => c.type === 'base');
  const baseBalance =
    baseCredit != null
      ? typeof baseCredit.balance === 'string'
        ? parseInt(baseCredit.balance, 10)
        : (baseCredit.balance ?? 0)
      : 1; // If no base credit in planDetails, allow (don't block)

  // Base credits are always required first
  const hasBaseCredits = baseBalance > 0;
  const hasPrototypeCredits = prototypeBalance > 0;

  // Final check: base credits required, then prototype credits in prototyper mode
  // If no base credits -> block (show UpgradeCtaCard)
  // If has base credits AND in prototyper mode -> also check prototype credits
  const canProceed = hasBaseCredits && (currentMode === 'prototyper' ? hasPrototypeCredits : true);
  // ========== Scroll-to-Bottom Hook ==========
  const {
    showButton: showScrollButton,
    opacity: scrollButtonOpacity,
    scrollToBottom,
  } = useScrollToBottom(
    conversationContainerRef,
    { threshold: 100, debounceMs: 50 },
    messages // Pass messages array directly (not wrapped in array)
  );

  // Right-hand split panel (prototype/artifact): open state, resize refs,
  // width animation, and close-on-return-to-clarify. `setIsPrototypeOpen` is
  // exposed so the streaming-orchestration effect below can open it.
  const {
    isOpen: isPrototypeOpen,
    setIsOpen: setIsPrototypeOpen,
    isSecondaryOpen,
    isConversationPanelCollapsed,
    setIsConversationPanelCollapsed,
    conversationPanelRef,
    prototypePanelRef,
    close: handleClosePrototype,
    reopen: handleReopenPrototype,
    toggleConversationPanel: handleToggleConversationPanel,
  } = usePrototypePanel({
    secondaryAlsoOpen: streamedArtifact.isOpen,
    currentMode,
    initiatedFromUncover,
    scrollToBottom,
    setSidebarCollapsed: setIsSidebarCollapsed,
  });

  const libraryRestoreOpenedRef = useRef(false);
  useEffect(() => {
    if (!openPrototypePanelFromLibrary) return;
    if (libraryRestoreOpenedRef.current) return;
    libraryRestoreOpenedRef.current = true;
    // Mode is already `prototyper` from the library URL (`?mode=prototyper`).
    // Do not call handleModeChange — on scope pages that navigates to `…/new`.
    setIsPrototypeOpen(true);
  }, [openPrototypePanelFromLibrary, setIsPrototypeOpen]);

  // ========== Effects ==========

  // Mutual exclusivity: the streamed-artifact panel and the prototype panel share
  // the same right-hand slot, so only one may be open at a time. Each effect is
  // keyed on ONE panel's open flag and closes the other when both are open — so
  // the panel that just opened (the one whose flag changed) wins, and the reverse
  // close never re-triggers the first (the guard is already false by then). Works
  // for every open path: a user clicking an artifact pill, or a prototype build
  // auto-opening its panel.
  useEffect(() => {
    if (streamedArtifact.isOpen && isPrototypeOpen) setIsPrototypeOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamedArtifact.isOpen]);
  useEffect(() => {
    if (isPrototypeOpen && streamedArtifact.isOpen) streamedArtifact.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrototypeOpen]);

  // Prototype-completion chime (self-contained).
  useConversationSound({
    isPrototypeCreating: isStreamingPrototype ? isWorkbenchBuildLive : false,
    isNewlyGenerated: isStreamingPrototype ? workbenchTimelineTerminalVisible : undefined,
    prototypeId: isStreamingPrototype ? streamingWorkbenchLlmInteractionId : currentPrototypeId,
  });

  // WebContainer build-stream orchestration (refs, plan helpers, the
  // build-start / deferred-start / reset effects, and the prototype action
  // handlers) lives in useConversationPrototypeStream. It reads facade/workbench
  // from context; the panel state + reply-active guard + derived values come in
  // as params.
  const {
    handleGeneratePrototypeFromUncover,
    handleBackToUncover,
    handleRetryPrototyper,
    handlePrototyperClick,
    resetBuildStreamLatch,
    continuingTurnId,
    continuingTurnLabel,
  } = useConversationPrototypeStream({
    isPrototypeOpen,
    setIsPrototypeOpen,
    isReplyStillActive,
    currentPrototypeId,
    hasPrototypeCredits,
  });

  // Message-turn lifecycle (initial message, submit, answer, feedback, ensure
  // conversation id, cancel) lives in useConversationTurns. It reads
  // facade/workbench from context; isReplyStillActive + resetBuildStreamLatch
  // (from the prototype-stream hook) come in as params.
  const {
    handleSubmit,
    handleAnswerSubmit,
    handleCancel,
    handleEnsureConversationId,
    handleFeedback,
  } = useConversationTurns({ isReplyStillActive, resetBuildStreamLatch });

  // "Fix with AI" on a crashed preview (`Workbench` dispatches the event) sends the crash as a
  // normal prototyper turn, the same as the founder typing it.
  useEffect(() => {
    if (!isStreamingPrototype) return undefined;
    const onFixPreviewCrash = (event: Event) => {
      const crash = (event as CustomEvent<WorkbenchPreviewCrash>).detail;
      if (!crash || isStreaming || isWorkbenchBuildLive) return;
      // Repair the crashed version directly: its build gets the error, the file and the
      // founder's unsaved edits (test finding 5). Sent as a chat message instead, Nexus planned
      // it as a new request and asked which screen was blank.
      const prototypeTurnIds = messages
        .filter(
          (m) => m.role === 'assistant' && m.llmInteractionId && messageHasPrototyperArtifact(m)
        )
        .map((m) => m.llmInteractionId as string);
      if (
        activeSnapshotInteractionId &&
        canRepairInPlace({
          activeTurnId: activeSnapshotInteractionId,
          turnIds: prototypeTurnIds,
          turnVersions: workbench.turnVersions,
        }) &&
        workbench.repairPreviewCrash?.(activeSnapshotInteractionId, crash)
      ) {
        return;
      }
      // An older version (a build would edit the prototype's newer files), or no build could
      // start: describe the crash as a request, as before.
      handleSubmit(buildPreviewCrashFixPrompt(crash));
    };
    window.addEventListener(WORKBENCH_FIX_PREVIEW_CRASH_EVENT, onFixPreviewCrash);
    return () => window.removeEventListener(WORKBENCH_FIX_PREVIEW_CRASH_EVENT, onFixPreviewCrash);
  }, [
    isStreamingPrototype,
    isStreaming,
    isWorkbenchBuildLive,
    handleSubmit,
    messages,
    activeSnapshotInteractionId,
    workbench.turnVersions,
    workbench.repairPreviewCrash,
  ]);

  // "Build prototype" when the preview only has the starter template (no saved version): the
  // same path as the prototype card's Retry, which starts the latest plan's build.
  useEffect(() => {
    if (!isStreamingPrototype) return undefined;
    const onBuildMissingVersion = () => handleRetryPrototyper();
    window.addEventListener(WORKBENCH_BUILD_MISSING_VERSION_EVENT, onBuildMissingVersion);
    return () =>
      window.removeEventListener(WORKBENCH_BUILD_MISSING_VERSION_EVENT, onBuildMissingVersion);
  }, [isStreamingPrototype, handleRetryPrototyper]);

  // The assistant turn that owns the artifact opened from the library.
  // A turn can carry several artifacts; the message only keeps the first, so
  // match the turn by interaction id as well as by artifact id.
  const libraryArtifactMessage = openArtifactIdFromLibrary
    ? messages.find(
        (message) =>
          message.role === 'assistant' &&
          (message.artifact?.id === openArtifactIdFromLibrary ||
            (openArtifactInteractionId != null &&
              message.llmInteractionId === openArtifactInteractionId))
      )
    : undefined;
  const libraryArtifactMessageIdRef = useRef<string | null>(null);
  libraryArtifactMessageIdRef.current =
    libraryArtifactMessage?.id ?? openArtifactInteractionId ?? null;

  // Library opens land on that turn. Every other reveal still jumps to the latest message.
  const scrollOnReveal = useCallback(() => {
    const messageId = libraryArtifactMessageIdRef.current;
    const root = conversationContainerRef.current;
    if (openArtifactIdFromLibrary && messageId && root) {
      const target = root.querySelector(`[data-message-id="${messageId}"]`);
      if (target instanceof HTMLElement) {
        target.scrollIntoView({ block: 'center' });
        return;
      }
    }
    scrollToBottom();
  }, [openArtifactIdFromLibrary, scrollToBottom]);

  // Skeleton visibility + scroll-to-bottom on first history load.
  const { showConversation } = useConversationHistoryView({
    currentConversationId,
    isLoadingDetails,
    messagesLength: messages.length,
    scrollToBottom: scrollOnReveal,
  });

  // Auto-open was removed: the panel now only opens when the user clicks
  // an ArtifactPill. Streamed artifacts stay collapsed until requested.

  /**
   * Close the artifact + prototype side panels when switching between
   * conversations — a panel belongs to the conversation you left, not the one
   * you're opening. Skips the initial mount and the new-chat null→id transition
   * (`prev` falsy), so a panel opened during the first turn isn't yanked shut
   * when the new conversation gets its id. Does NOT skip the reverse — leaving
   * an existing conversation (`prev` truthy) for a brand-new, not-yet-created
   * one (`currentConversationId` null) — otherwise a panel left open over an
   * old chat stays open, now empty, over the fresh "New chat" screen.
   */
  const prevConversationIdForPanelsRef = useRef(currentConversationId);
  useEffect(() => {
    const prev = prevConversationIdForPanelsRef.current;
    prevConversationIdForPanelsRef.current = currentConversationId;
    if (prev && prev !== currentConversationId) {
      streamedArtifact.close();
      setIsPrototypeOpen(false);
    }
  }, [currentConversationId, streamedArtifact, setIsPrototypeOpen]);

  // Library click (`?artifact=`): open that document once this conversation's
  // history is actually loaded. The first paint still holds the previous chat,
  // and treating that as "not found" toasted and then refused to retry — so
  // only artifacts already in the cached chat opened.
  const openArtifactPanelRef = useRef(streamedArtifact.open);
  openArtifactPanelRef.current = streamedArtifact.open;
  const openedLibraryArtifactIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openArtifactIdFromLibrary) return;
    if (openedLibraryArtifactIdRef.current === openArtifactIdFromLibrary) return;
    if (!libraryConversationId || currentConversationId !== libraryConversationId) return;
    if (isLoadingDetails || messages.length === 0) return;

    if (libraryArtifactMessage?.artifact?.id === openArtifactIdFromLibrary) {
      openedLibraryArtifactIdRef.current = openArtifactIdFromLibrary;
      openArtifactPanelRef.current(libraryArtifactMessage.artifact);
      return;
    }

    // The message keeps only the first artifact on the turn. The one that was
    // clicked may be a later sibling, loaded by the turn id.
    if (!openArtifactInteractionId) {
      openedLibraryArtifactIdRef.current = openArtifactIdFromLibrary;
      showToast({ message: 'Could not open this artifact.', severity: 'error' });
      return;
    }

    let cancelled = false;
    void artifactService.listArtifactsByLlmInteraction(openArtifactInteractionId).then(
      (rows) => {
        if (cancelled) return;
        const row = rows.find((item) => item.id === openArtifactIdFromLibrary);
        if (!row) {
          openedLibraryArtifactIdRef.current = openArtifactIdFromLibrary;
          showToast({ message: 'Could not open this artifact.', severity: 'error' });
          return;
        }
        openedLibraryArtifactIdRef.current = openArtifactIdFromLibrary;
        openArtifactPanelRef.current(asMessageArtifact(row));
      },
      () => {
        if (cancelled) return;
        openedLibraryArtifactIdRef.current = openArtifactIdFromLibrary;
        showToast({ message: 'Could not open this artifact.', severity: 'error' });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [
    openArtifactIdFromLibrary,
    openArtifactInteractionId,
    libraryConversationId,
    currentConversationId,
    isLoadingDetails,
    messages.length,
    libraryArtifactMessage,
  ]);

  // Opening the side panel resizes the chat and then scrolls to the latest
  // message. Put the view back on the library artifact's turn once, after that
  // layout pass. `?artifact=` stays in the URL, so without the guard a later
  // pill open in the same chat would jump back here too.
  const libraryArtifactScrolledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openArtifactIdFromLibrary || !streamedArtifact.isOpen) return;
    if (libraryArtifactScrolledRef.current === openArtifactIdFromLibrary) return;
    const artifactId = openArtifactIdFromLibrary;
    const timer = setTimeout(() => {
      libraryArtifactScrolledRef.current = artifactId;
      const messageId = libraryArtifactMessageIdRef.current;
      const root = conversationContainerRef.current;
      const target =
        messageId && root ? root.querySelector(`[data-message-id="${messageId}"]`) : null;
      if (target instanceof HTMLElement) target.scrollIntoView({ block: 'center' });
    }, 700);
    return () => clearTimeout(timer);
  }, [openArtifactIdFromLibrary, streamedArtifact.isOpen]);

  /**
   * Clear prototyper state when conversation changes or component unmounts
   */
  useEffect(() => {
    // Clear prototyper state when conversation changes
    return () => {
      // Cleanup on unmount or conversation change
      facade.prototyper.cancelPrototype();
    };
  }, [currentConversationId]); // Remove facade.prototyper from deps

  // Inline Design Insights panel width (percentage of the total area)
  const insightsWidthPercent = 25; // adjust as needed

  // ========== Handlers ==========

  /**
   * Handle getting project metadata (matching old implementation)
   */
  /**
   * Handle back button click — stop any in-flight stream and go back in
   * history. (The old project-creation back flow was retired: a conversation
   * always belongs to a startup now.)
   */
  const handleBackClick = useCallback(() => {
    handleCancel();
    // A page-provided override (scope conversation → Work Scopes) wins over the
    // default history pop, so back is deterministic instead of "wherever the
    // browser history happens to point".
    if (onBack) {
      onBack();
      return;
    }
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  }, [handleCancel, onBack]);

  /**
   * Intercept browser back button to show custom dialog
   * When user clicks browser back button, prevent navigation and show custom dialog
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Check if we're in a plugin context (data: URL or null origin)
    const isPluginContext =
      window.location.origin === 'null' ||
      window.location.protocol === 'data:' ||
      window.location.href.startsWith('data:');

    if (isPluginContext) {
      return;
    }

    try {
      // Push a state when component mounts to enable back button interception
      window.history.pushState(null, '', window.location.href);
    } catch (error) {
      // Silently fail in plugin context where pushState is not supported
      console.debug('pushState not available:', error);
      return;
    }

    const handlePopState = () => {
      // Show custom dialog instead of navigating back
      handleBackClick();

      try {
        // Push current state back to prevent navigation
        // This keeps the user on the current page
        window.history.pushState(null, '', window.location.href);
      } catch (error) {
        // Silently fail if pushState is not available
        console.debug('pushState not available in popstate handler:', error);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isWeb, handleBackClick]);

  /**
   * Handle prototyper retry (fired from the error banner's retry action).
   */
  const handleRetryDrawing = useCallback(
    (drawType: string) => {
      if (!currentConversationId) return;
      if (drawType === 'prototyper') {
        handleRetryPrototyper();
      }
    },
    [currentConversationId, handleRetryPrototyper]
  );

  /** Unpublish the version on screen only (its own link); other versions stay published. */
  const handleUnpublishVersion = useCallback(
    async (versionId: string) => {
      if (!currentPrototypeId) return;
      try {
        await facade.prototyper.unpublishVersion(currentPrototypeId, versionId);
      } catch (error) {
        const detail = error instanceof Error ? error.message : undefined;
        showToast({
          message: detail && detail !== 'Request failed' ? detail : 'Failed to unpublish this version',
          severity: 'error',
        });
        throw error;
      }
    },
    [facade.prototyper, currentPrototypeId, showToast]
  );

  /**
   * Handle publish prototype
   */
  const handlePublishPrototype = useCallback(
    async (slug: string | null, options?: { republish?: boolean }) => {
      const prototyperId = currentPrototypeId;

      try {
        if (
          (slug || options?.republish) &&
          isStreamingPrototype &&
          currentProjectId &&
          currentConversationId
        ) {
          // The streaming/WebContainer publish path doesn't need a pre-existing demo-link id
          // (`publishToDemo` only needs a slug) — a resumed/restored build never gets one via
          // SSE, so requiring it here would block publish even though it isn't necessary.
          const publishSlug = slug ?? currentPrototypeSlug;
          if (!publishSlug) {
            throw new Error('No slug available for publish');
          }
          const snapshotId = await uploadVerifiedPrototypeSnapshot?.();
          // The version on screen is the one published: the backend writes its prototype's
          // one link. Without the turn it used the conversation's newest link, which is how
          // publishing one prototype landed under another's (test finding 1).
          const published = await facade.prototyper.publishToDemo(
            currentProjectId,
            currentConversationId,
            publishSlug,
            snapshotId,
            activeSnapshotInteractionId ?? undefined
          );
          // Record it on the turn that owns this prototype. Without this the publish lives only
          // in the store, and anything that rebuilds that state from the conversation — a
          // prototype switch, a restore — showed the prototype as unpublished again until a
          // page reload.
          onPrototypePublished?.({
            demoLinkId: published.demoLinkId,
            slug: published.slug,
          });
        } else {
          if (!prototyperId) {
            showToast({
              message: 'No prototype ID available',
              severity: 'error',
            });
            throw new Error('No prototype ID available');
          }
          await facade.prototyper.updatePrototyperDemoLink(prototyperId, slug || null);
          // Unpublish keeps the link row, so only the slug changes hands here.
          onPrototypePublished?.({ demoLinkId: prototyperId, slug: slug || null });
        }
      } catch (error) {
        console.error('Error publishing prototype:', error);
        // `prototyperService.handleError` already unwraps the server's structured
        // `{ code: 'PROTOTYPE_BUILD_FAILED', message, detail }` publish errors into a plain
        // `Error(detail)` — surface that instead of a flat generic message so a build failure
        // (missing package.json, a real vite error, …) is actually legible to the user.
        const fallback =
          slug === null ? 'Failed to unpublish prototype' : 'Failed to publish prototype';
        const detail = error instanceof Error ? error.message : undefined;
        showToast({
          message: detail && detail !== 'Request failed' ? detail : fallback,
          severity: 'error',
        });
        throw error;
      }
    },
    [
      facade.prototyper,
      showToast,
      currentPrototypeId,
      currentPrototypeSlug,
      isStreamingPrototype,
      currentProjectId,
      currentConversationId,
      uploadVerifiedPrototypeSnapshot,
      onPrototypePublished,
      activeSnapshotInteractionId,
    ]
  );

  // ========== Computed Values ==========

  const backRoute = currentProjectId ? `/projects/${currentProjectId}` : '/';

  const shouldShowBanner = () => {
    const userMessages = messages.filter((msg) => msg.role === 'user');
    return !currentProjectId && userMessages.length >= 2 && showBanner;
  };

  // Set banner properties on last message if there's a prototyper error
  const messagesWithBanner = React.useMemo(() => {
    if (!facade.prototyper.prototypeError || messages.length === 0) return messages;

    const lastMessage = messages[messages.length - 1];
    const hasPrototyperMode =
      (lastMessage as any).prototypeReady === true || (lastMessage as any).prototyper_demo_link_id;

    if (!hasPrototyperMode) return messages;

    // Clone messages array and update last message with banner properties
    const updatedMessages = [...messages];
    updatedMessages[updatedMessages.length - 1] = {
      ...lastMessage,
      hasBanner: true,
      bannerType: 'error',
      bannerPosition: 'bottom',
      drawType: 'prototyper',
    } as any;

    return updatedMessages;
  }, [messages, facade.prototyper.prototypeError]);

  // const getLastMessageMinHeight = (index: number): string => {
  //   if (index !== messages.length - 1) return '';

  //   if (isWeb) {
  //     return shouldShowBanner && !isReadyToCreate
  //       ? 'min-h-[calc(100vh-360px)]'
  //       : 'min-h-[calc(100vh-300px)]';
  //   } else {
  //     return shouldShowBanner && !isReadyToCreate
  //       ? 'min-h-[calc(100vh-272px)]'
  //       : 'min-h-[calc(100vh-227px)]';
  //   }
  // };

  const conversationDirection = React.useMemo<'rtl' | 'ltr' | undefined>(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.detectedLanguage) {
        return message.detectedLanguage;
      }

      if (message.content && hasArabicCharacters(message.content)) {
        return 'rtl';
      }
    }

    if (name && hasArabicCharacters(name)) {
      return 'rtl';
    }

    if (init_message && hasArabicCharacters(init_message)) {
      return 'rtl';
    }

    return undefined;
  }, [messages, name, init_message]);

  // ========== Render ==========

  // Ownership / missing conversation (Nest or AI 403/404) — after all hooks
  // (an early return above hooks causes "Rendered fewer hooks than expected").
  //
  // Scope workspace stays on its own URL: a bad/forbidden chat inside a real
  // startup must NOT unmount the sidebar / header / recent chats. Instead we
  // render the normal shell and show an inline "Conversation not found" state
  // in the messages pane (see the `conversationNotFoundInline` branch below),
  // and the scope page clears `?chat=` via `onConversationNotFound`.
  //
  // Non-scope (`/conversation/:id`) has no scope shell to stay in, so a
  // full-screen 404 is the right UX.
  const isScopeMode = facade.sidebar?.pageType === 'scope';
  const conversationNotFoundInline = isScopeMode && facade.state.conversationNotFound;

  if (facade.state.conversationNotFound && !isScopeMode) {
    return (
      <NotFoundState
        description="Conversation not found"
        actionLabel="Back to My Startups"
        onAction={() => facade.navigation.toProjectsList?.()}
      />
    );
  }

  return (
    <>
      <div
        // className="bg-white"
        style={
          {
            '--sidebar-width': isSidebarCollapsed
              ? 'var(--sidebar-width-collapsed)'
              : 'var(--sidebar-width-expanded)',
            // Zero the findings gutter whenever the panel isn't rendered
            // so the main column reclaims the space instead of showing an
            // empty 360px strip on the right.
            ...(isFindingsOpen &&
            !isPrototypeOpen &&
            !streamedArtifact.isOpen &&
            !isFindingsCollapsed
              ? {}
              : { '--sidebar-width-findings': '0px' }),
          } as React.CSSProperties
        }
      >
        {/* Sidebar */}
        <SideMenu
          items={(() => {
            let items = facade.sidebar.navigationItems || [];
            if (isOnboardingUser) {
              // Remove mode items
              items = items.filter((item) => item.type !== 'mode');
              // Add 'new chat' item if not present
              if (!items.some((item) => item.id === 'new-chat')) {
                items = [
                  {
                    id: 'new-chat',
                    label: 'New',
                    icon: CirclePlus,
                    type: 'navigation',
                    onClick: () => {
                      // Act like back arrow: use the same handler
                      handleBackClick();
                    },
                  },
                  ...items,
                ];
              }
            }
            return items;
          })()}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          socialLinks={facade.sidebar.socialLinks}
          onLogoClick={facade.sidebar.onNavigateToLanding}
          user={{
            isLoggedin: isLoggedIn,
            name: facade.header.headerProps.user?.name,
            avatar: facade.header.headerProps.user?.avatar,
          }}
          userMenuProps={{
            points: facade.header.headerProps.points,
            pointsLabel: 'Points',
            menuItems: facade.header.userMenuItems || [],
            socialLinks: facade.header.userMenuSocialLinks || [],
            onRequestUpgrade: facade.header.onRequestUpgrade,
            isRequestingUpgrade: facade.header.isRequestingUpgrade || false,
            canRequestUpgrade: true,
            isWeb,
          }}
        />

        {/* Main Content */}
        <main className="flex-1 overflow-hidden transition-[margin-inline-start] duration-200 ease-in-out ms-[var(--sidebar-width)] me-[var(--sidebar-width-findings)]">
          {/* Main Content - Split or Full */}
          <ResizablePanelGroup direction="horizontal" className="flex-1 !h-screen">
            {/* Conversation Panel */}
            <ResizablePanel
              ref={conversationPanelRef}
              defaultSize={isSecondaryOpen ? CONVERSATION_PANEL_DEFAULT_PERCENT : 100}
              minSize={0}
              maxSize={isSecondaryOpen ? 70 : 100}
              collapsible
              collapsedSize={0}
              onCollapse={() => setIsConversationPanelCollapsed(true)}
              onExpand={() => setIsConversationPanelCollapsed(false)}
              className="!overflow-auto h-full scrollbar-chat relative"
            >
              <div className="min-h-full flex flex-col">
                {/* Header only spans the chat column — avoids overlapping the workbench */}
                <div className="sticky top-0 z-30 shrink-0 bg-background">
                  {/* <div className="from-gray-25 via-gray-25 via-65% to-gray-25/0 pointer-events-none absolute inset-0 -bottom-5 z-[-1] bg-gradient-to-b blur-sm"></div> */}
                  <Header
                    {...facade.header.headerProps}
                    showBackButton
                    backRoute={backRoute}
                    backText={
                      facade.state.scopeTitle
                        ? facade.state.conversationTitle
                          ? `${facade.state.scopeTitle} / ${facade.state.conversationTitle}`
                          : facade.state.scopeTitle
                        : name || 'New Conversation'
                    }
                    onBack={handleBackClick}
                    isWeb={isWeb}
                    isHaderMenu={(facade.scopeHeaderMenu?.length ?? 0) > 0}
                    menuItems={facade.scopeHeaderMenu ?? []}
                    className={cn('relative w-full transition-all duration-200 ease-in-out')}
                    creditLimitBanner={
                      creditLimitBanner.isVisible
                        ? {
                            ...creditLimitBanner,
                            onAction: () => facade.navigation.toBilling(),
                            onDismiss: () => creditLimitBanner.setVisible(false),
                          }
                        : undefined
                    }
                  />
                </div>

                <div className="flex min-h-[calc(100vh-65px)]">
                  <div
                    className={`max-w-landing-page w-full mx-auto pt-4 pb-2 px-6 flex-1 flex flex-col`}
                  >
                    <>
                      {/* Scope mode: bad/forbidden chat stays in the scope
                         * shell — show an inline "Conversation not found"
                         * empty state instead of the skeleton / messages.
                         * "Start a new chat" clears `?chat=` via the scope
                         * page's onConversationNotFound callback. */}
                        {conversationNotFoundInline ? (
                          <div className="flex-1 min-h-0 flex items-center justify-center">
                            <NotFoundState
                              inline
                              description="Conversation not found"
                              actionLabel="Start a new chat"
                              onAction={() => onConversationNotFound?.()}
                            />
                          </div>
                        ) : (
                          <>
                            <MessagesPane
                              continuingTurnId={continuingTurnId}
                              continuingTurnLabel={continuingTurnLabel}
                              findings={findings}
                              conversationContainerRef={conversationContainerRef}
                              showConversation={showConversation}
                              messages={messagesWithBanner}
                              projectId={currentProjectId}
                              isLoading={isLoading}
                              isStreaming={isStreaming}
                              isClarification={isClarification}
                              isReflection={isReflection}
                              isRepeated={isRepeated}
                              isReadyToCreate={isReadyToCreate}
                              isPrototyper={isPrototyper}
                              shouldShowBanner={shouldShowBanner()}
                              isPrototypeOpen={isPrototypeOpen}
                              onPrototypeReturn={handleReopenPrototype}
                              onPrototyperClick={handlePrototyperClick}
                              onStreamedArtifactOpen={streamedArtifact.open}
                              onSubmit={handleSubmit}
                              onFeedback={handleFeedback}
                              onRetryDrawing={handleRetryDrawing}
                            />

                            {/* Input Area — the interactive question set is passed
                            into the composer's sticky container (via
                            `questionSlot`) so it stays pinned just above the
                            textbox. The composer stays usable so the founder can
                            answer in prose instead. */}
                            <ConversationComposer
                              containerRef={chatBoxContainerRef}
                              draftRequest={hotEditAskDraft}
                              questionSlot={
                                facade.streaming.questionSet ? (
                                  <InteractiveQuestionCard
                                    questionSet={facade.streaming.questionSet}
                                    onSubmit={handleAnswerSubmit}
                                    onDismiss={facade.streaming.clearQuestionSet}
                                    isSubmitting={isStreaming}
                                    error={facade.streaming.questionAnswerError}
                                    onErrorDismiss={facade.streaming.clearQuestionAnswerError}
                                  />
                                ) : undefined
                              }
                              showScrollButton={showScrollButton}
                              scrollButtonOpacity={scrollButtonOpacity}
                              onScrollToBottom={scrollToBottom}
                              canProceed={canProceed}
                              hasBaseCredits={hasBaseCredits}
                              onUpgrade={facade.navigation.toBilling}
                              onSubmit={handleSubmit}
                              conversationId={currentConversationId}
                              onEnsureConversationId={handleEnsureConversationId}
                              currentMode={currentMode}
                              isLoading={isLoading}
                              isStreaming={isStreaming}
                              isPrototypeCreating={isPrototypeCreating}
                              onCancel={handleCancel}
                              isWeb={isWeb}
                              llmFamily={llmFamily as any}
                              onLlmFamilyChange={(newModel) => {
                                facade.conversation.setLlmFamily(newModel);
                              }}
                              isPrototypeOpen={isPrototypeOpen}
                              onGeneratePrototype={handleGeneratePrototypeFromUncover}
                              prototypeProductTypes={prototypeProductTypes}
                              onBackToUncover={handleBackToUncover}
                              hasPrototypeCredits={hasPrototypeCredits}
                              hasProject={!!currentProjectId}
                              initiatedFromUncover={initiatedFromUncover}
                              prototyperGenerationModel={facade.prototyper.prototypeGenerationModel}
                              onPrototyperGenerationModelChange={
                                facade.prototyper.setPrototypeGenerationModel
                              }
                              onFileUploaded={() => {
                                void facade.actions.refreshFindingsPanel();
                              }}
                            />
                          </>
                        )}
                      </>
                  </div>
                </div>
              </div>
            </ResizablePanel>

            {/* Resize Handle */}
            {isSecondaryOpen && <ResizableHandle withHandle />}

            {/* Prototype Panel or Web Drawing Artifact */}
            <ResizablePanel
              ref={prototypePanelRef}
              defaultSize={isSecondaryOpen ? PROTOTYPE_PANEL_DEFAULT_PERCENT : 0}
              minSize={isSecondaryOpen ? 30 : 0}
              maxSize={100}
              collapsible
              collapsedSize={0}
              className={`${isSecondaryOpen ? 'opacity-100 bg-white' : 'opacity-0 pointer-events-none'}`}
              aria-hidden={!isSecondaryOpen}
              onCollapse={() => setIsConversationPanelCollapsed(false)}
            >
              <PrototyperPane
                isPrototypeOpen={isPrototypeOpen}
                isConversationPanelCollapsed={isConversationPanelCollapsed}
                onClose={handleClosePrototype}
                onPublish={handlePublishPrototype}
                onUnpublishVersion={handleUnpublishVersion}
                onToggleConversationPanel={handleToggleConversationPanel}
                currentPrototypeId={currentPrototypeId}
                currentPrototypeSlug={currentPrototypeSlug}
                currentProjectId={currentProjectId}
                findings={findings}
                streamedArtifact={streamedArtifact}
                conversationDirection={conversationDirection}
                onOpenVersion={handlePrototyperClick}
              />
            </ResizablePanel>

            {/* Design Insights renders inline inside the resizable group; the
                durable Scope Intelligence panel is the fixed ScopeIntelligenceRail
                below (outside the group so its position doesn't fight the resize
                math). */}
            {findings.isDesignInsightsPanelOpen && (
              <DesignInsightsPanel
                open={findings.isDesignInsightsPanelOpen}
                onClose={findings.closeDesignInsights}
                inline
                data={facade.designInsights.insights || undefined}
                isLoading={facade.designInsights.isLoading}
              />
            )}
          </ResizablePanelGroup>
        </main>
        {/* Fixed right-hand Scope Intelligence rail (durable panel + collapse FAB). */}
        <ScopeIntelligenceRail
          findings={findings}
          isPrototypeOpen={isPrototypeOpen}
          streamedArtifactOpen={streamedArtifact.isOpen}
        />
      </div>

      <ConversationDialogs
        authDialogState={authDialogState}
        isUpgradeToSmartOpen={isUpgradeToSmartDialogOpen}
        onCloseUpgradeToSmart={() => setIsUpgradeToSmartDialogOpen(false)}
        onConfirmUpgradeToSmart={() => {
          setIsUpgradeToSmartDialogOpen(false);
          facade.navigation.toBilling();
        }}
        planDetails={planDetails}
      />
    </>
  );
}

export default ConversationModule;
